/**
 * Web Vitals tracking and performance monitoring utilities
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

// Performance thresholds based on Google's recommendations
const PERFORMANCE_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  INP: { good: 200, needsImprovement: 500 },   // Interaction to Next Paint (replaces FID)
  CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  TTFB: { good: 800, needsImprovement: 1800 }  // Time to First Byte
};

type MetricName = keyof typeof PERFORMANCE_THRESHOLDS;

interface PerformanceData {
  metric: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  url: string;
  userAgent: string;
}

// Performance data storage
let performanceData: PerformanceData[] = [];

// Get performance rating based on thresholds
const getPerformanceRating = (metric: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const thresholds = PERFORMANCE_THRESHOLDS[metric];
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsImprovement) return 'needs-improvement';
  return 'poor';
};

// Send metric to analytics service
const sendToAnalytics = (data: PerformanceData) => {
  // In a real application, you would send this to your analytics service
  // For now, we'll just log it and store it locally
  console.log('📊 Web Vitals:', data);
  
  // Store locally for debugging
  performanceData.push(data);
  
  // Keep only last 100 measurements
  if (performanceData.length > 100) {
    performanceData = performanceData.slice(-100);
  }
  
  // Send to analytics service (example)
  if (process.env.NODE_ENV === 'production') {
    // Example: Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', data.metric, {
        event_category: 'Web Vitals',
        value: Math.round(data.value),
        custom_parameter_1: data.rating,
        non_interaction: true,
      });
    }
    
    // Example: Custom analytics endpoint
    fetch('/api/analytics/web-vitals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).catch(() => {
      // Silently handle analytics failures
    });
  }
};

// Create metric handler
const createMetricHandler = (metricName: MetricName) => (metric: Metric) => {
  const data: PerformanceData = {
    metric: metricName,
    value: metric.value,
    rating: getPerformanceRating(metricName, metric.value),
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
  
  sendToAnalytics(data);
};

// Initialize Web Vitals tracking
export const initWebVitals = () => {
  if (typeof window === 'undefined') return;

  try {
    onCLS(createMetricHandler('CLS'));
    onINP(createMetricHandler('INP'));
    onFCP(createMetricHandler('FCP'));
    onLCP(createMetricHandler('LCP'));
    onTTFB(createMetricHandler('TTFB'));
  } catch (error) {
    console.warn('Failed to initialize Web Vitals tracking:', error);
  }
};

// Get current performance data
export const getPerformanceData = (): PerformanceData[] => {
  return [...performanceData];
};

// Get performance summary
export const getPerformanceSummary = () => {
  if (performanceData.length === 0) {
    return null;
  }
  
  const summary = performanceData.reduce((acc, data) => {
    if (!acc[data.metric]) {
      acc[data.metric] = {
        values: [],
        ratings: { good: 0, 'needs-improvement': 0, poor: 0 }
      };
    }
    
    acc[data.metric].values.push(data.value);
    acc[data.metric].ratings[data.rating]++;
    
    return acc;
  }, {} as Record<MetricName, { values: number[]; ratings: Record<string, number> }>);
  
  // Calculate averages and percentiles
  const result = Object.entries(summary).map(([metric, data]) => {
    const values = data.values.sort((a, b) => a - b);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    const p75 = values[Math.floor(values.length * 0.75)];
    const p95 = values[Math.floor(values.length * 0.95)];
    
    return {
      metric: metric as MetricName,
      average: Math.round(avg),
      p75: Math.round(p75),
      p95: Math.round(p95),
      ratings: data.ratings,
      sampleSize: values.length
    };
  });
  
  return result;
};

// Performance budget checker
export const checkPerformanceBudget = () => {
  const summary = getPerformanceSummary();
  if (!summary) return null;
  
  const violations = summary.filter(metric => {
    const threshold = PERFORMANCE_THRESHOLDS[metric.metric];
    return metric.p75 > threshold.needsImprovement;
  });
  
  return {
    passed: violations.length === 0,
    violations: violations.map(v => ({
      metric: v.metric,
      actual: v.p75,
      threshold: PERFORMANCE_THRESHOLDS[v.metric].needsImprovement,
      severity: v.p75 > PERFORMANCE_THRESHOLDS[v.metric].needsImprovement * 1.5 ? 'high' : 'medium'
    }))
  };
};

// Custom performance marks for user timing
export const markPerformance = (name: string) => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    performance.mark(name);
  }
};

export const measurePerformance = (name: string, startMark: string, endMark?: string) => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    try {
      if (endMark) {
        performance.measure(name, startMark, endMark);
      } else {
        performance.measure(name, startMark);
      }
      
      // Get the measurement
      const measures = performance.getEntriesByName(name, 'measure');
      if (measures.length > 0) {
        const measure = measures[measures.length - 1];
        console.log(`⏱️ ${name}: ${Math.round(measure.duration)}ms`);
        return measure.duration;
      }
    } catch (error) {
      console.warn(`Failed to measure performance for ${name}:`, error);
    }
  }
  return null;
};

// React component performance tracker
export const trackComponentPerformance = (componentName: string) => {
  const startTime = performance.now();
  
  return {
    end: () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (duration > 16) { // Longer than one frame at 60fps
        console.warn(`🐌 Slow component render: ${componentName} took ${Math.round(duration)}ms`);
      }
      
      return duration;
    }
  };
};

// Memory usage tracker (Chrome only)
export const getMemoryUsage = () => {
  if (typeof window !== 'undefined' && 'performance' in window && (performance as any).memory) {
    const memory = (performance as any).memory;
    return {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024), // MB
      percentage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
    };
  }
  return null;
};

// Performance observer for long tasks
export const observeLongTasks = () => {
  if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) { // Tasks longer than 50ms
            console.warn(`🐌 Long task detected: ${Math.round(entry.duration)}ms`);
          }
        }
      });
      
      observer.observe({ entryTypes: ['longtask'] });
      return observer;
    } catch (error) {
      console.warn('Long task observation not supported:', error);
    }
  }
  return null;
};

// Initialize all performance monitoring
export const initPerformanceMonitoring = () => {
  initWebVitals();
  observeLongTasks();
  
  // Log memory usage periodically in development
  if (process.env.NODE_ENV === 'development') {
    setInterval(() => {
      const memory = getMemoryUsage();
      if (memory && memory.percentage > 80) {
        console.warn('🚨 High memory usage:', memory);
      }
    }, 30000); // Every 30 seconds
  }
};
